import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Send, FileDown, Settings2, Clock, Coins, Zap, CheckCircle } from "lucide-react";
import { fmtINR } from "@/lib/format";
import { toast } from "sonner";

const KIND_META = {
  overdue_bill:  { label: "Overdue", tint: "bg-red-500/10 text-red-800 border-red-500/25", icon: AlertTriangle },
  advance_voucher: { label: "Advance", tint: "bg-amber-500/10 text-amber-800 border-amber-500/25", icon: Coins },
  requested:     { label: "Requested", tint: "bg-blue-500/10 text-blue-800 border-blue-500/25", icon: Send },
};

const STATUS_TINT = {
  not_submitted: "text-muted-foreground",
  pending: "text-amber-700",
  submitted: "text-blue-700",
  approved: "text-emerald-700",
  rejected: "text-red-700",
};

export default function PaymentSheet() {
  const [sheet, setSheet] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [submitBusy, setSubmitBusy] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [columns, setColumns] = useState([]);
  const [available, setAvailable] = useState([]);

  const load = async () => {
    try {
      const r = await api.get("/payment-sheet");
      setSheet(r.data);
      setSelected(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  const loadConfig = async () => {
    try {
      const r = await api.get("/payment-sheet/config");
      setColumns(r.data?.columns || []);
      setAvailable(r.data?.available || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); loadConfig(); }, []);

  const allRows = sheet ? [
    ...(sheet.overdue_bills || []),
    ...(sheet.advance_vouchers || []),
    ...(sheet.requested_transactions || []),
  ] : [];

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = (bucket) => {
    const ids = bucket.map(r => r.id);
    const allChecked = ids.every(id => selected.has(id));
    const next = new Set(selected);
    ids.forEach(id => { if (allChecked) next.delete(id); else next.add(id); });
    setSelected(next);
  };

  const submitSelected = async () => {
    if (selected.size === 0) return toast.error("Pick at least one row");
    setSubmitBusy(true);
    try {
      const r = await api.post("/payment-sheet/submit", { row_ids: Array.from(selected) });
      toast.success(`Submitted ${r.data.total_submitted} · Skipped ${r.data.total_skipped}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    setSubmitBusy(false);
  };

  const markApproved = async (rowId) => {
    if (!window.confirm("Admin bypass: mark this row as approved without going through the approval inbox?")) return;
    try { await api.post(`/payment-sheet/mark-approved/${encodeURIComponent(rowId)}`); toast.success("Marked approved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadXlsx = () => {
    const a = document.createElement("a");
    a.href = `${API}/payment-sheet/payable-list.xlsx`;
    a.target = "_blank"; a.rel = "noopener";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const saveConfig = async () => {
    try { await api.put("/payment-sheet/config", { columns }); toast.success("Columns saved"); setConfigOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const RowGrid = ({ rows, kind }) => {
    const meta = KIND_META[kind];
    const Icon = meta.icon;
    if (rows.length === 0) return <div className="text-xs text-muted-foreground py-4 text-center">No rows.</div>;
    const allChecked = rows.every(r => selected.has(r.id));
    return (
      <div className="overflow-auto">
        <table className="w-full dense">
          <thead>
            <tr>
              <th style={{ width: 30 }}><Checkbox checked={allChecked} onCheckedChange={() => toggleAll(rows)} data-testid={`toggle-all-${kind}`} /></th>
              <th>Vendor</th><th>Bill</th><th>Due</th><th className="text-right">Amount</th>
              <th className="text-right">TDS</th><th className="text-right">Net Payable</th>
              <th>Bank / IFSC</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`ps-row-${r.id}`} className={selected.has(r.id) ? "bg-accent/5" : ""}>
                <td><Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`ps-check-${r.id}`} /></td>
                <td>
                  <div className="font-medium">{r.vendor_name || "—"}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">{r.vendor_gstin || "—"}</div>
                </td>
                <td>
                  <div className="font-mono text-xs">{r.bill_no || "—"}</div>
                  <div className="text-[10px] text-muted-foreground">{r.bill_date}</div>
                </td>
                <td className="text-xs">{r.due_date}</td>
                <td className="text-right tabular-nums">{fmtINR(r.amount)}</td>
                <td className="text-right tabular-nums text-muted-foreground">{r.tds_amount > 0 ? fmtINR(r.tds_amount) : "—"}</td>
                <td className="text-right tabular-nums font-medium">{fmtINR(r.net_payable)}</td>
                <td className="text-[10px]">
                  <div className="font-mono">{r.bank_name || "—"}</div>
                  <div className="text-muted-foreground">{r.ifsc || "—"}</div>
                </td>
                <td>
                  <span className={`text-[10px] font-mono uppercase ${STATUS_TINT[r.approval_status] || "text-muted-foreground"}`}>
                    {(r.approval_status || "not_submitted").replace("_", " ")}
                  </span>
                </td>
                <td className="text-right">
                  {(r.approval_status === "pending" || r.approval_status === "submitted") && (
                    <button onClick={() => markApproved(r.id)} className="text-[10px] text-accent hover:underline" data-testid={`ps-mark-${r.id}`}>Mark ✓</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!sheet) return <div className="p-6 text-sm text-muted-foreground">Loading payment sheet…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="payment-sheet-page">
      <PageHeader eyebrow="Cash-out" title="Payment Approval Sheet"
                  description="Overdue bills + advance vouchers + manually-requested payments in one view. Submit selected rows for approval, then download the payable list for bank upload." />

      <div className="grid md:grid-cols-4 gap-3">
        <Card className="rounded-sm p-3 border border-red-500/25 bg-red-500/5" data-testid="kpi-overdue">
          <div className="text-[10px] font-mono uppercase text-red-800 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Overdue bills</div>
          <div className="text-2xl font-bold tabular-nums mt-1 text-red-900">{sheet.totals.overdue_count}</div>
          <div className="text-xs text-red-700 tabular-nums">{fmtINR(sheet.totals.overdue_amount)}</div>
        </Card>
        <Card className="rounded-sm p-3 border border-amber-500/25 bg-amber-500/5" data-testid="kpi-advance">
          <div className="text-[10px] font-mono uppercase text-amber-800 flex items-center gap-1"><Coins className="w-3 h-3" /> Advance vouchers</div>
          <div className="text-2xl font-bold tabular-nums mt-1 text-amber-900">{sheet.totals.advance_count}</div>
          <div className="text-xs text-amber-700 tabular-nums">{fmtINR(sheet.totals.advance_amount)}</div>
        </Card>
        <Card className="rounded-sm p-3 border border-blue-500/25 bg-blue-500/5" data-testid="kpi-requested">
          <div className="text-[10px] font-mono uppercase text-blue-800 flex items-center gap-1"><Send className="w-3 h-3" /> Requested</div>
          <div className="text-2xl font-bold tabular-nums mt-1 text-blue-900">{sheet.totals.requested_count}</div>
          <div className="text-xs text-blue-700 tabular-nums">{fmtINR(sheet.totals.requested_amount)}</div>
        </Card>
        <Card className="rounded-sm p-3">
          <div className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1"><Zap className="w-3 h-3" /> Selected</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{selected.size}</div>
          <div className="flex gap-2 mt-2">
            <Button size="sm" onClick={submitSelected} disabled={submitBusy || selected.size === 0}
                    className="rounded-sm h-7 text-xs gap-1 bg-accent hover:bg-accent/90 text-white" data-testid="ps-submit-btn">
              <Send className="w-3 h-3" /> {submitBusy ? "Submitting…" : "Submit for approval"}
            </Button>
          </div>
        </Card>
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => setConfigOpen(true)} className="rounded-sm gap-1" data-testid="ps-config-btn">
          <Settings2 className="w-3.5 h-3.5" /> Configure columns
        </Button>
        <Button size="sm" onClick={downloadXlsx} className="rounded-sm gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ps-download-btn">
          <FileDown className="w-3.5 h-3.5" /> Payable list · Excel
        </Button>
      </div>

      <Card className="rounded-sm">
        <div className="border-b bg-red-500/5 px-4 py-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-800" />
          <span className="font-heading font-semibold text-red-900">Overdue bills</span>
          <span className="text-[10px] font-mono uppercase text-red-700">// {sheet.totals.overdue_count} · {fmtINR(sheet.totals.overdue_amount)}</span>
        </div>
        <RowGrid rows={sheet.overdue_bills} kind="overdue_bill" />
      </Card>

      <Card className="rounded-sm">
        <div className="border-b bg-amber-500/5 px-4 py-2 flex items-center gap-2">
          <Coins className="w-4 h-4 text-amber-800" />
          <span className="font-heading font-semibold text-amber-900">Advance payment vouchers</span>
          <span className="text-[10px] font-mono uppercase text-amber-700">// {sheet.totals.advance_count} · {fmtINR(sheet.totals.advance_amount)}</span>
        </div>
        <RowGrid rows={sheet.advance_vouchers} kind="advance_voucher" />
      </Card>

      <Card className="rounded-sm">
        <div className="border-b bg-blue-500/5 px-4 py-2 flex items-center gap-2">
          <Send className="w-4 h-4 text-blue-800" />
          <span className="font-heading font-semibold text-blue-900">Specific transaction requests</span>
          <span className="text-[10px] font-mono uppercase text-blue-700">// {sheet.totals.requested_count} · from vendor bill "Request Payment"</span>
        </div>
        <RowGrid rows={sheet.requested_transactions} kind="requested" />
      </Card>

      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="ps-config-dialog">
          <DialogHeader><DialogTitle>Configure payable list columns</DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground mb-2">Tick columns to include in the Excel export.</div>
          <div className="max-h-96 overflow-y-auto space-y-1.5 pr-2">
            {columns.map((c, idx) => (
              <div key={c.key} className="flex items-center gap-2 border rounded-sm px-2 py-1.5 text-xs">
                <Checkbox checked={c.enabled} onCheckedChange={(v) => {
                  const next = [...columns]; next[idx] = { ...c, enabled: !!v }; setColumns(next);
                }} data-testid={`col-${c.key}`} />
                <div className="flex-1"><span className="font-mono text-[10px] text-muted-foreground">{c.key}</span> · {c.label}</div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setConfigOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-accent hover:bg-accent/90 text-white" onClick={saveConfig} data-testid="ps-save-config">
              Save columns
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
